import { MAX_SCRIPTURE_REFS } from '../../config/constants';
import type { TBibleBook, TScripture, TScriptureRef } from '../../types';
import { bookName, chapterCount, scriptureBooks, verseCount } from '../../utils';
import type {
  IRefNumberFieldSpec,
  IScriptureRefBounds,
  TNumberCommit,
  TRefNumberField,
  TRefNumberReducer,
} from './types';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Clamps every dependent field of a ref so coerceScripture accepts it unchanged. */
const normalizeRef = (ref: TScriptureRef): TScriptureRef => {
  const chapters = chapterCount(ref.book);
  const chapterStart = clamp(ref.chapterStart, 1, chapters);
  const chapterEnd = clamp(ref.chapterEnd, chapterStart, chapters);
  if (ref.verseStart === null) {
    return { ...ref, chapterStart, chapterEnd, verseStart: null, verseEnd: null };
  }
  const verseStart = clamp(ref.verseStart, 1, verseCount(ref.book, chapterStart));
  const minEnd = chapterStart === chapterEnd ? verseStart : 1;
  const verseEnd = clamp(ref.verseEnd ?? verseStart, minEnd, verseCount(ref.book, chapterEnd));
  return { ...ref, chapterStart, chapterEnd, verseStart, verseEnd };
};

/** Builds a new value from a refs list, recomputing books. */
const withRefs = (value: TScripture, refs: TScriptureRef[]): TScripture => ({
  refs,
  freeform: value.freeform,
  books: scriptureBooks(refs),
});

/** Replaces the ref at `index` with the normalized output of `update`. */
const updateRef = (
  value: TScripture,
  index: number,
  update: (ref: TScriptureRef) => TScriptureRef
): TScripture => {
  const current = value.refs[index];
  if (!current) return value;
  return withRefs(
    value,
    value.refs.map((ref, i) => (i === index ? normalizeRef(update({ ...ref })) : { ...ref }))
  );
};

const wholeChapter = (book: TBibleBook, chapter: number): TScriptureRef => ({
  book,
  bookName: bookName(book),
  chapterStart: chapter,
  chapterEnd: chapter,
  verseStart: null,
  verseEnd: null,
});

/** The min and max each number field of a ref accepts. */
export const refBounds = (ref: TScriptureRef): IScriptureRefBounds => {
  const chapters = chapterCount(ref.book);
  const sameChapter = ref.chapterStart === ref.chapterEnd;
  return {
    chapterStart: { min: 1, max: chapters },
    chapterEnd: { min: ref.chapterStart, max: chapters },
    verseStart: { min: 1, max: verseCount(ref.book, ref.chapterStart) },
    verseEnd: {
      min: sameChapter ? (ref.verseStart ?? 1) : 1,
      max: verseCount(ref.book, ref.chapterEnd),
    },
  };
};

/** Sets the book of the ref at `index` (chapter 1, whole chapter); `index === refs.length` appends. */
export const selectRefBook = (value: TScripture, index: number, book: TBibleBook): TScripture => {
  if (index < 0 || index > value.refs.length) return value;
  const refs = value.refs.map((ref) => ({ ...ref }));
  refs[index] = wholeChapter(book, 1);
  return withRefs(value, refs);
};

/** Sets the start chapter, pulling chapterEnd up and re-bounding verses; '' is ignored. */
export const setRefChapterStart = (
  value: TScripture,
  index: number,
  chapter: TNumberCommit
): TScripture => {
  if (chapter === '') return value;
  return updateRef(value, index, (ref) => ({
    ...ref,
    chapterStart: chapter,
    chapterEnd: Math.max(ref.chapterEnd, chapter),
  }));
};

/** Sets the end chapter, floored at chapterStart and re-bounding verseEnd; '' is ignored. */
export const setRefChapterEnd = (
  value: TScripture,
  index: number,
  chapter: TNumberCommit
): TScripture => {
  if (chapter === '') return value;
  return updateRef(value, index, (ref) => ({ ...ref, chapterEnd: chapter }));
};

/** Sets the start verse (pairing verseEnd when verses were null); '' clears both verses. */
export const setRefVerseStart = (
  value: TScripture,
  index: number,
  verse: TNumberCommit
): TScripture =>
  updateRef(value, index, (ref) =>
    verse === ''
      ? { ...ref, verseStart: null, verseEnd: null }
      : { ...ref, verseStart: verse, verseEnd: ref.verseEnd ?? verse }
  );

/** Sets the end verse (pairing verseStart at 1 when verses were null); '' makes a single verse. */
export const setRefVerseEnd = (
  value: TScripture,
  index: number,
  verse: TNumberCommit
): TScripture =>
  updateRef(value, index, (ref) => {
    if (verse === '') return { ...ref, verseEnd: ref.verseStart };
    return { ...ref, verseStart: ref.verseStart ?? 1, verseEnd: verse };
  });

/** Appends the chapter after the last ref, in its book; a no-op when empty or at the cap. */
export const addRef = (value: TScripture): TScripture => {
  const last = value.refs.at(-1);
  if (!last || value.refs.length >= MAX_SCRIPTURE_REFS) return value;
  const chapter = Math.min(last.chapterEnd + 1, chapterCount(last.book));
  return withRefs(value, [
    ...value.refs.map((ref) => ({ ...ref })),
    wholeChapter(last.book, chapter),
  ]);
};

/** Reads a number input's raw text as a commit: '' when cleared, null when it is not a number. */
export const parseNumberCommit = (raw: string): TNumberCommit | null => {
  const text = raw.trim();
  if (text === '') return '';
  const parsed = Number(text);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

/** Drops the ref at `index`. */
export const removeRef = (value: TScripture, index: number): TScripture =>
  withRefs(
    value,
    value.refs.filter((_, i) => i !== index).map((ref) => ({ ...ref }))
  );

/** Sets the free-text fallback. */
export const setFreeform = (value: TScripture, freeform: string): TScripture =>
  withRefs(
    { ...value, freeform },
    value.refs.map((ref) => ({ ...ref }))
  );

/** The number fields of a ref row, in the order they render. */
export const REF_NUMBER_FIELDS: readonly IRefNumberFieldSpec[] = [
  { field: 'chapterStart', label: 'Chapter', placeholder: 'Ch.' },
  { field: 'verseStart', label: 'Verse', placeholder: 'Verse' },
  { field: 'chapterEnd', label: 'End chapter', placeholder: 'To ch.' },
  { field: 'verseEnd', label: 'End verse', placeholder: 'To verse' },
];

/** The reducer that commits each number field. */
export const REF_NUMBER_REDUCERS: Record<TRefNumberField, TRefNumberReducer> = {
  chapterStart: setRefChapterStart,
  chapterEnd: setRefChapterEnd,
  verseStart: setRefVerseStart,
  verseEnd: setRefVerseEnd,
};
